/**
 * Latency probe: input-to-paint p95 measurement.
 *
 * M2.T12 per IMPLEMENTATION_PLAN §7 M2.T12.
 *
 * Uses PerformanceObserver watching 'event' entries
 * (PerformanceEventTiming API). Logs input-to-paint p95 to the console.
 *
 * CI median gate: ≤ 16ms on Linux runner.
 * Manual gate at M2 exit: p95 ≤ 16ms on real iPad.
 *
 * The probe is non-intrusive: it installs a passive observer that
 * accumulates event durations and reports stats every `reportIntervalMs`.
 */

/** Event timing sample in ms. */
export type EventTimingSample = {
  eventType: string;
  duration: number;
  processingStart: number;
  startTime: number;
};

export type LatencyStats = {
  sampleCount: number;
  median: number;
  p95: number;
  max: number;
};

/**
 * Calculates p-th percentile of a sorted numeric array.
 * Array must be sorted ascending before calling.
 */
export function percentile(sortedValues: number[], p: number): number {
  if (sortedValues.length === 0) return 0;
  const idx = Math.ceil((p / 100) * sortedValues.length) - 1;
  return sortedValues[Math.max(0, idx)]!;
}

/**
 * Compute summary stats from a list of duration samples.
 */
export function computeLatencyStats(durations: number[]): LatencyStats {
  if (durations.length === 0) {
    return { sampleCount: 0, median: 0, p95: 0, max: 0 };
  }
  const sorted = [...durations].sort((a, b) => a - b);
  return {
    sampleCount: sorted.length,
    median: percentile(sorted, 50),
    p95: percentile(sorted, 95),
    max: sorted[sorted.length - 1]!,
  };
}

export type LatencyProbeOptions = {
  /** How often to log stats (ms). Default 5000. */
  reportIntervalMs?: number;
  /** Event types to watch. Default ['pointerdown', 'pointermove']. */
  eventTypes?: string[];
  /** Called with stats on each report interval. Default: console.log. */
  onReport?: (stats: LatencyStats) => void;
};

/**
 * Install a PerformanceObserver that tracks event-to-paint latency.
 * Returns a `stop()` function to clean up.
 *
 * In environments without PerformanceObserver or PerformanceEventTiming
 * (e.g. jsdom, older Safari), the probe silently no-ops.
 */
export function installLatencyProbe(options: LatencyProbeOptions = {}): {
  stop: () => void;
  getSamples: () => number[];
} {
  const {
    reportIntervalMs = 5000,
    eventTypes = ["pointerdown", "pointermove"],
    onReport = (stats) => {
      // Only log if there's data worth reporting
      if (stats.sampleCount > 0) {
        console.log(
          `[latency-probe] n=${stats.sampleCount} median=${stats.median.toFixed(1)}ms p95=${stats.p95.toFixed(1)}ms max=${stats.max.toFixed(1)}ms`,
        );
        if (stats.p95 > 16) {
          console.warn(`[latency-probe] ⚠️ p95 ${stats.p95.toFixed(1)}ms exceeds 16ms target`);
        }
      }
    },
  } = options;

  const samples: number[] = [];
  let observer: PerformanceObserver | null = null;
  let intervalHandle: ReturnType<typeof setInterval> | null = null;

  try {
    if (typeof PerformanceObserver === "undefined") return { stop: () => {}, getSamples: () => [] };

    // Check PerformanceEventTiming is supported
    const supportedTypes = PerformanceObserver.supportedEntryTypes ?? [];
    if (!supportedTypes.includes("event")) return { stop: () => {}, getSamples: () => [] };

    observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as PerformanceEventTiming[]) {
        if (eventTypes.includes(entry.name)) {
          // duration = end of processing - startTime; approximates input-to-paint
          samples.push(entry.duration);
        }
      }
    });

    observer.observe({ type: "event", buffered: false } as PerformanceObserverInit);

    intervalHandle = setInterval(() => {
      const stats = computeLatencyStats(samples.slice());
      onReport(stats);
    }, reportIntervalMs);
  } catch {
    // PerformanceObserver construction can fail in some environments
  }

  function stop() {
    if (observer) {
      try {
        observer.disconnect();
      } catch {
        /* ignore */
      }
      observer = null;
    }
    if (intervalHandle !== null) {
      clearInterval(intervalHandle);
      intervalHandle = null;
    }
  }

  return { stop, getSamples: () => samples.slice() };
}
